<script setup lang="ts">
/**
 * Navigation latérale (ordinateur et tablette). Deux largeurs : dépliée (icône + libellé) ou
 * repliée (icônes seules, libellé en infobulle et pour les lecteurs d'écran). Séparée du contenu
 * par un filet, sans ombre. Carte du compte en bas.
 */
import { computed } from 'vue'
import { RouterLink, type RouteLocationRaw } from 'vue-router'

import { fr } from '@/copy/fr'

import type { IconName } from '../icons/registry'
import Icon from './Icon.vue'

export interface SideNavItem {
  to: RouteLocationRaw
  label: string
  icon: IconName
}

export interface SideNavSection {
  title: string
  items: readonly SideNavItem[]
}

const props = defineProps<{
  sections: readonly SideNavSection[]
  addLabel: string
  account: { name: string; detail: string; to: RouteLocationRaw }
}>()

defineEmits<{ add: [] }>()

const collapsed = defineModel<boolean>('collapsed', { default: false })

const initial = computed(() => (props.account.name.trim()[0] ?? '?').toLocaleUpperCase('fr-FR'))

const LINK =
  'flex h-11 items-center gap-3 rounded-control text-body text-ink-muted transition-colors duration-80 hover:bg-surface active:bg-surface-strong'
</script>

<template>
  <aside
    :class="[
      'sticky top-0 flex h-dvh shrink-0 flex-col border-r border-line bg-bg py-5 transition-[width] duration-240 ease-standard',
      collapsed ? 'w-20 px-3' : 'w-64 px-4',
    ]"
  >
    <div :class="['flex h-10 items-center', collapsed ? 'justify-center' : 'justify-between px-2']">
      <span v-if="!collapsed" class="text-section font-semibold text-accent-strong">{{
        fr.app.name
      }}</span>
      <span
        v-else
        class="flex size-9 items-center justify-center rounded-control bg-accent-strong text-label font-semibold text-ink-inverse"
        aria-hidden="true"
        >C</span
      >
      <button
        v-if="!collapsed"
        type="button"
        class="flex size-8 items-center justify-center rounded-full border border-line text-ink-muted transition-colors duration-80 hover:bg-surface"
        :aria-label="fr.ui.collapseMenu"
        @click="collapsed = true"
      >
        <Icon name="back" :size="16" />
      </button>
    </div>
    <button
      v-if="collapsed"
      type="button"
      class="mx-auto mt-3 flex size-8 items-center justify-center rounded-full border border-line text-ink-muted transition-colors duration-80 hover:bg-surface"
      :aria-label="fr.ui.expandMenu"
      @click="collapsed = false"
    >
      <Icon name="forward" :size="16" />
    </button>

    <button
      type="button"
      :class="[
        'mt-6 flex h-11 items-center justify-center gap-2 rounded-control bg-accent-strong text-label text-ink-inverse transition-colors duration-80 active:bg-accent-pressed',
        collapsed ? 'w-11 self-center' : 'w-full',
      ]"
      :aria-label="collapsed ? addLabel : undefined"
      :title="collapsed ? addLabel : undefined"
      @click="$emit('add')"
    >
      <Icon name="add" />
      <span v-if="!collapsed">{{ addLabel }}</span>
    </button>

    <nav class="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto" :aria-label="fr.ui.mainNavigation">
      <div v-for="section in sections" :key="section.title" class="flex flex-col gap-1">
        <p
          v-if="!collapsed"
          class="px-3 pb-1 text-caption font-medium tracking-wider text-ink-muted uppercase"
        >
          {{ section.title }}
        </p>
        <span
          v-else
          class="mx-auto mb-2 block h-px w-8 bg-line"
          role="separator"
          :aria-label="section.title"
        />
        <RouterLink
          v-for="item in section.items"
          :key="item.label"
          :to="item.to"
          :class="[LINK, collapsed ? 'w-11 justify-center self-center' : 'px-3']"
          active-class="bg-surface text-ink!"
          :aria-label="collapsed ? item.label : undefined"
          :title="collapsed ? item.label : undefined"
        >
          <Icon :name="item.icon" />
          <span v-if="!collapsed">{{ item.label }}</span>
        </RouterLink>
      </div>
    </nav>

    <RouterLink
      :to="account.to"
      :class="[
        'mt-4 flex items-center gap-3 rounded-control border border-line transition-colors duration-80 hover:bg-surface',
        collapsed ? 'size-14 justify-center self-center' : 'p-2.5',
      ]"
      :aria-label="collapsed ? account.name : undefined"
    >
      <span
        class="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-label font-semibold text-accent-strong"
        aria-hidden="true"
        >{{ initial }}</span
      >
      <span v-if="!collapsed" class="flex min-w-0 flex-1 flex-col">
        <span class="truncate text-label text-ink">{{ account.name }}</span>
        <span class="truncate text-caption text-ink-muted">{{ account.detail }}</span>
      </span>
      <Icon v-if="!collapsed" name="forward" :size="16" class="text-ink-muted" />
    </RouterLink>
  </aside>
</template>
